/* ---------------------------------------------------------------------------
   The bridge dashboard's location map: one structure, and the three things that
   explain where it stands — the road it carries, the river it crosses, and the
   shape of the ground in contours. Nothing else.

   WHY NOT ONE OF THE TWO BASE MAPS. OpenStreetMap's standard style and
   OpenTopoMap are raster tiles: their buildings, shops, land use and labels are
   pixels, and a pixel cannot be switched off. Drawing only roads and rivers means
   having them as VECTORS and styling them here, so this map reads OpenStreetMap
   through OpenFreeMap — free, keyless, no account, the OpenMapTiles schema — and
   draws two of its layers and none of the rest.

   THE CONTOURS COST NO NEW DATA. They are traced in the browser by
   maplibre-contour out of the same Terrarium DEM tiles `/api/terrain` already
   proxies for the Fly and Site views. Roughly 30 m SRTM underneath, so they read
   the shape of a valley and say nothing about a deck level.

   THE SAME MOUNTING ARRANGEMENT as ./flyMap.ts: a slot marked `data-keep`, a map
   built into it after the render, rebuilt only when the structure or the theme
   changes.
   --------------------------------------------------------------------------- */

import type { BridgeView, Theme } from '../data/types.ts';
import { DEM_MAX_ZOOM } from './terrain.ts';

/** OpenFreeMap's planet TileJSON. Sends `Access-Control-Allow-Origin: *`. */
const VECTOR_TILEJSON = 'https://tiles.openfreemap.org/planet';
/** OpenFreeMap's glyphs, so road, river and contour labels need no server of ours. */
const GLYPHS = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';
const FONT = ['Noto Sans Regular'];
const FONT_ITALIC = ['Noto Sans Italic'];

/** What the card credits, as parts so the links stay links. */
export const SITE_MAP_CREDIT: readonly { text: string; href?: string }[] = [
  { text: '© OpenFreeMap', href: 'https://openfreemap.org' },
  { text: '© OpenMapTiles', href: 'https://www.openmaptiles.org/' },
  { text: '© OpenStreetMap contributors', href: 'https://www.openstreetmap.org/copyright' },
  { text: 'contours: Mapzen terrarium / SRTM' },
];

/** The colours, per theme. A style is plain data, so these cannot be CSS tokens. */
interface Palette {
  land: string;
  water: string;
  waterLine: string;
  waterLabel: string;
  contour: string;
  contourIndex: string;
  contourLabel: string;
  roadCasing: string;
  roadMajor: string;
  roadMinor: string;
  roadLabel: string;
  halo: string;
}

const PALETTE: Record<Theme, Palette> = {
  light: {
    land: '#f6f3ec',
    water: '#a9cbe6',
    waterLine: '#4f8fc4',
    waterLabel: '#2f6c9f',
    contour: '#c9a982',
    contourIndex: '#a8805a',
    contourLabel: '#8a6542',
    roadCasing: '#8c8c8c',
    roadMajor: '#f2b84b',
    roadMinor: '#ffffff',
    roadLabel: '#333333',
    halo: '#f6f3ec',
  },
  dark: {
    land: '#1d1f22',
    water: '#1f3a52',
    waterLine: '#4b86b8',
    waterLabel: '#8fb9de',
    contour: '#5a4c3c',
    contourIndex: '#7d6a52',
    contourLabel: '#a08c72',
    roadCasing: '#0e0f10',
    roadMajor: '#b8893a',
    roadMinor: '#6c6f74',
    roadLabel: '#d8d8d8',
    halo: '#1d1f22',
  },
};

/** Road classes drawn, most important last so they sit on top. Rail and paths are not roads. */
const MINOR_ROADS = ['track', 'service', 'minor'];
const MAJOR_ROADS = ['tertiary', 'secondary', 'primary', 'trunk', 'motorway'];

/** Line width by zoom for a road of this weight (1 = minor, 2 = major). */
function roadWidth(weight: 1 | 2, casing: boolean): unknown {
  const pad = casing ? 2 : 0;
  return ['interpolate', ['exponential', 1.6], ['zoom'], 10, weight * 0.6 + pad * 0.3, 14, weight * 2.5 + pad, 17, weight * 9 + pad * 1.5];
}

/** Where the roads, the water and the label glyphs come from. */
export interface VectorBase {
  /** A MapLibre vector source in the OpenMapTiles schema. */
  source: Record<string, unknown>;
  glyphs: string;
}

/** OpenFreeMap, which the dashboard's map reads. */
export const OPENFREEMAP: VectorBase = { source: { type: 'vector', url: VECTOR_TILEJSON }, glyphs: GLYPHS };

/**
 * The whole style. The contour tile URL comes from maplibre-contour, which
 * registers a protocol for it — see `build`.
 */
export function siteStyle(theme: Theme, contourTiles: string, base: VectorBase = OPENFREEMAP): Record<string, unknown> {
  const c = PALETTE[theme];
  const roadFilter = (classes: readonly string[]) => ['in', ['get', 'class'], ['literal', classes]];
  return {
    version: 8,
    glyphs: base.glyphs,
    sources: {
      omt: base.source,
      contours: { type: 'vector', tiles: [contourTiles], maxzoom: 15 },
    },
    layers: [
      { id: 'land', type: 'background', paint: { 'background-color': c.land } },

      /* The ground. Minor lines every interval, index lines heavier and labelled. */
      {
        id: 'contour',
        type: 'line',
        source: 'contours',
        'source-layer': 'contours',
        filter: ['==', ['get', 'level'], 0],
        paint: { 'line-color': c.contour, 'line-width': 0.6 },
      },
      {
        id: 'contour-index',
        type: 'line',
        source: 'contours',
        'source-layer': 'contours',
        filter: ['>', ['get', 'level'], 0],
        paint: { 'line-color': c.contourIndex, 'line-width': 1.2 },
      },

      /* The water. Polygons for rivers wide enough to have banks, lines for the rest. */
      { id: 'water', type: 'fill', source: 'omt', 'source-layer': 'water', paint: { 'fill-color': c.water } },
      {
        id: 'waterway',
        type: 'line',
        source: 'omt',
        'source-layer': 'waterway',
        filter: ['!=', ['get', 'brunnel'], 'tunnel'],
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': c.waterLine,
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            10, ['match', ['get', 'class'], 'river', 1.2, 0.4],
            15, ['match', ['get', 'class'], 'river', 4, 1.5],
            17, ['match', ['get', 'class'], 'river', 8, 3],
          ],
          'line-dasharray': ['case', ['==', ['get', 'intermittent'], 1], ['literal', [3, 2]], ['literal', [1, 0]]],
        },
      },

      /* The roads, cased so they read over contours. */
      {
        id: 'road-minor-casing',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: roadFilter(MINOR_ROADS),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': c.roadCasing, 'line-width': roadWidth(1, true) },
      },
      {
        id: 'road-major-casing',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: roadFilter(MAJOR_ROADS),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': c.roadCasing, 'line-width': roadWidth(2, true) },
      },
      {
        id: 'road-minor',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: roadFilter(MINOR_ROADS),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': c.roadMinor, 'line-width': roadWidth(1, false) },
      },
      {
        id: 'road-major',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: roadFilter(MAJOR_ROADS),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': c.roadMajor, 'line-width': roadWidth(2, false) },
      },

      /* Labels last: contour heights, river names, road names and numbers. */
      {
        id: 'contour-label',
        type: 'symbol',
        source: 'contours',
        'source-layer': 'contours',
        filter: ['>', ['get', 'level'], 0],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['concat', ['number-format', ['get', 'ele'], {}], ' m'],
          'text-font': FONT,
          'text-size': 10,
        },
        paint: { 'text-color': c.contourLabel, 'text-halo-color': c.halo, 'text-halo-width': 1.5 },
      },
      {
        id: 'waterway-label',
        type: 'symbol',
        source: 'omt',
        'source-layer': 'waterway',
        filter: ['has', 'name'],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
          'text-font': FONT_ITALIC,
          'text-size': 12,
          'text-letter-spacing': 0.1,
        },
        paint: { 'text-color': c.waterLabel, 'text-halo-color': c.halo, 'text-halo-width': 1.5 },
      },
      {
        id: 'road-label',
        type: 'symbol',
        source: 'omt',
        'source-layer': 'transportation_name',
        filter: roadFilter([...MINOR_ROADS, ...MAJOR_ROADS]),
        layout: {
          'symbol-placement': 'line',
          'text-field': [
            'case',
            ['all', ['has', 'ref'], ['has', 'name']], ['concat', ['get', 'name'], '  ', ['get', 'ref']],
            ['coalesce', ['get', 'name'], ['get', 'ref']],
          ],
          'text-font': FONT,
          'text-size': 11,
        },
        paint: { 'text-color': c.roadLabel, 'text-halo-color': c.halo, 'text-halo-width': 1.5 },
      },
    ],
  };
}

/* --------------------------- mounting --------------------------- */

interface SiteMap {
  addControl(control: unknown, position?: string): void;
  on(event: string, fn: (e: unknown) => void): void;
  remove(): void;
}

let map: SiteMap | null = null;
let canvas: HTMLElement | null = null;
let marker: { remove(): void } | null = null;
let host: HTMLElement | null = null;
let showing: { id: string; theme: Theme } | null = null;
let building = 0;
/** maplibre-contour registers its protocol on the library once per page. */
let contourTiles: string | null = null;

async function build(slot: HTMLElement, b: BridgeView & { lat: number; lng: number }, theme: Theme, token: number): Promise<void> {
  const [maplibre, contour] = await Promise.all([
    import('maplibre-gl'),
    import('maplibre-contour'),
    import('maplibre-gl/dist/maplibre-gl.css'),
  ]);
  if (token !== building) return;

  if (contourTiles === null) {
    const dem = new contour.default.DemSource({
      /* Absolute, because the tiles are fetched from a worker, whose base URL is a blob. */
      url: `${location.origin}/api/terrain?z={z}&x={x}&y={y}`,
      encoding: 'terrarium',
      maxzoom: DEM_MAX_ZOOM,
      worker: true,
    });
    dem.setupMaplibre(maplibre as never);
    contourTiles = dem.contourProtocolUrl({
      /* Minor and index interval in metres, by zoom. */
      thresholds: { 11: [50, 250], 12: [20, 100], 14: [10, 50] },
      elevationKey: 'ele',
      levelKey: 'level',
      contourLayer: 'contours',
    });
  }

  const container = document.createElement('div');
  container.className = 'bsite__canvas';
  slot.appendChild(container);
  canvas = container;

  const created = new maplibre.Map({
    container,
    style: siteStyle(theme, contourTiles) as never,
    center: [b.lng, b.lat],
    zoom: 15,
    minZoom: 9,
    maxZoom: 17.5,
    dragRotate: false,
    pitchWithRotate: false,
    attributionControl: false,
    hash: false,
  });
  created.touchZoomRotate.disableRotation();
  map = created as unknown as SiteMap;
  map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-left');
  map.addControl(new maplibre.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');

  const pin = document.createElement('div');
  pin.className = 'bsite__pin';
  pin.title = `${b.id} — ${b.name}`;
  marker = new maplibre.Marker({ element: pin }).setLngLat([b.lng, b.lat]).addTo(created) as unknown as { remove(): void };
}

function dispose(): void {
  marker?.remove();
  marker = null;
  map?.remove();
  map = null;
  canvas?.remove();
  canvas = null;
  host = null;
  showing = null;
}

/** Reconcile the location map with state, after the render. */
export function syncSiteMap(wanted: { bridge: BridgeView; theme: Theme } | null): void {
  const slot = document.querySelector<HTMLElement>('.bsite__slot');
  const b = wanted?.bridge;
  if (!wanted || !slot || !b || b.lat == null || b.lng == null) {
    if (map || showing) dispose();
    building++;
    return;
  }
  if (host === slot && showing?.id === b.id && showing.theme === wanted.theme) return;

  dispose();
  host = slot;
  showing = { id: b.id, theme: wanted.theme };
  const token = ++building;
  void build(slot, b as BridgeView & { lat: number; lng: number }, wanted.theme, token).catch(() => {
    /* A device with no WebGL, or a chunk that would not load: the card keeps its
       coordinates and credit, and `showing` stays set so this is not retried on
       every repaint. */
  });
}
